import type { Corners, MeshWarp, Point } from '../types.js';

// =============================================================================
// Calibration v2 — mesh 세분화 warp 유틸 (PLAN §4.2).
// identity 생성 / identity 판정 / 밀도 변경(bilinear 보간 승계).
// DOM 비의존 순수 함수.
// =============================================================================

/**
 * (cols+1)×(rows+1) row-major 균등 격자(identity) 메시를 만든다.
 * identity 메시는 렌더 시 no-op — 코너핀만 쓸 때 비용 0.
 */
export function createIdentityMesh(cols: number, rows: number): MeshWarp {
  const points: Point[] = [];
  for (let r = 0; r <= rows; r++) {
    for (let c = 0; c <= cols; c++) {
      points.push({ x: c / cols, y: r / rows });
    }
  }
  return { cols, rows, points };
}

/**
 * 현재 코너핀 quad를 따라가는 메시를 만든다. MeshWarp.points는 최종
 * destination-global 좌표이므로, 메시 모드 진입 시 출력 경계가 코너핀에서
 * 갑자기 풀스크린으로 바뀌지 않게 bilinear로 초기화한다.
 */
export function createMeshFromCorners(
  corners: Corners,
  cols: number,
  rows: number,
): MeshWarp {
  const [tl, tr, br, bl] = corners;
  const points: Point[] = [];
  for (let r = 0; r <= rows; r++) {
    const v = r / rows;
    for (let c = 0; c <= cols; c++) {
      const u = c / cols;
      const top = {
        x: tl.x + (tr.x - tl.x) * u,
        y: tl.y + (tr.y - tl.y) * u,
      };
      const bottom = {
        x: bl.x + (br.x - bl.x) * u,
        y: bl.y + (br.y - bl.y) * u,
      };
      points.push({
        x: top.x + (bottom.x - top.x) * v,
        y: top.y + (bottom.y - top.y) * v,
      });
    }
  }
  return { cols, rows, points };
}

function signedArea2(a: Point, b: Point, c: Point): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

/** 모든 셀의 두 삼각형이 같은 방향이고 충분한 면적을 갖는지 확인한다. */
export function isMeshTopologyValid(mesh: MeshWarp, epsilon = 1e-8): boolean {
  const stride = mesh.cols + 1;
  if (mesh.cols < 1 || mesh.rows < 1) return false;
  if (mesh.points.length !== stride * (mesh.rows + 1)) return false;
  for (let r = 0; r < mesh.rows; r++) {
    for (let c = 0; c < mesh.cols; c++) {
      const i00 = r * stride + c;
      const i10 = i00 + 1;
      const i01 = i00 + stride;
      const i11 = i01 + 1;
      if (
        signedArea2(mesh.points[i00]!, mesh.points[i10]!, mesh.points[i11]!) <= epsilon ||
        signedArea2(mesh.points[i00]!, mesh.points[i11]!, mesh.points[i01]!) <= epsilon
      ) {
        return false;
      }
    }
  }
  return true;
}

/**
 * 모든 정점이 균등 격자 위치에서 epsilon 이내인지 판정한다.
 * 점 개수가 (cols+1)×(rows+1)과 다르면(손상 데이터) false.
 */
export function isIdentityMesh(mesh: MeshWarp, epsilon = 1e-6): boolean {
  const expected = (mesh.cols + 1) * (mesh.rows + 1);
  if (mesh.points.length !== expected) return false;
  for (let r = 0; r <= mesh.rows; r++) {
    for (let c = 0; c <= mesh.cols; c++) {
      const p = mesh.points[r * (mesh.cols + 1) + c]!;
      if (
        Math.abs(p.x - c / mesh.cols) > epsilon ||
        Math.abs(p.y - r / mesh.rows) > epsilon
      ) {
        return false;
      }
    }
  }
  return true;
}

/**
 * 파라메트릭 (u,v) ∈ [0,1]² 위치의 메시 점을 bilinear 보간으로 샘플한다.
 * 에디터 히트테스트/resample 공용.
 */
export function sampleMeshPoint(mesh: MeshWarp, u: number, v: number): Point {
  const stride = mesh.cols + 1;
  const gx = Math.min(Math.max(u, 0), 1) * mesh.cols;
  const gy = Math.min(Math.max(v, 0), 1) * mesh.rows;
  // 마지막 행/열의 u=1, v=1도 유효한 셀 안에 들어가도록 클램프.
  const c0 = Math.min(Math.floor(gx), mesh.cols - 1);
  const r0 = Math.min(Math.floor(gy), mesh.rows - 1);
  const fx = gx - c0;
  const fy = gy - r0;
  const p00 = mesh.points[r0 * stride + c0]!;
  const p10 = mesh.points[r0 * stride + c0 + 1]!;
  const p01 = mesh.points[(r0 + 1) * stride + c0]!;
  const p11 = mesh.points[(r0 + 1) * stride + c0 + 1]!;
  const top = { x: p00.x + (p10.x - p00.x) * fx, y: p00.y + (p10.y - p00.y) * fx };
  const bot = { x: p01.x + (p11.x - p01.x) * fx, y: p01.y + (p11.y - p01.y) * fx };
  return { x: top.x + (bot.x - top.x) * fy, y: top.y + (bot.y - top.y) * fy };
}

/**
 * 그리드 밀도 변경. 기존 메시의 변위를 bilinear 보간으로 새 밀도에 승계한다.
 * 업샘플은 무손실(기존 점을 지나는 매끈한 면 유지), 다운샘플은 셀 내부의
 * 세부 변위가 소실될 수 있다 (UI에서 확인 모달 — PLAN §4.2).
 */
export function resampleMesh(mesh: MeshWarp, newCols: number, newRows: number): MeshWarp {
  const points: Point[] = [];
  for (let r = 0; r <= newRows; r++) {
    for (let c = 0; c <= newCols; c++) {
      points.push(sampleMeshPoint(mesh, c / newCols, r / newRows));
    }
  }
  return { cols: newCols, rows: newRows, points };
}
